import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, MessageCircle, Plus, Send, Loader2, ArrowLeft, ImagePlus, Pencil, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { api, formatApiError, fileUrl } from "../lib/api";
import { FeedVideo } from "../components/FeedVideo";
import { useAuth } from "../context/AuthContext";
import { AppHeader } from "../components/AppHeader";
import { BottomSheet } from "../components/BottomSheet";
import { MediaUploader } from "../components/MediaUploader";
import { Poll } from "../components/Poll";
import { PollComposer, pollPayload, BarChart3 } from "../components/PollComposer";
import { parseMentions } from "../lib/mentions";
import { MentionInput } from "../components/MentionInput";

const ago = (d) => { try { return formatDistanceToNow(new Date(d), { addSuffix: true }); } catch { return ""; } };
const initial = (n) => (n || "?").charAt(0).toUpperCase();

export default function Hangouts() {
  const { user, family } = useAuth();
  const canPoll = family?.can_poll;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState(false);
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");
  const [posting, setPosting] = useState(false);
  const [active, setActive] = useState(null); // opened thread post
  const [editPost, setEditPost] = useState(null); // post being edited
  const [editContent, setEditContent] = useState("");
  const [editImage, setEditImage] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [usePoll, setUsePoll] = useState(false);
  const [pollQuestions, setPollQuestions] = useState([{ type: "mc", text: "", options: ["", ""] }]);
  const [members, setMembers] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => { api.get("/families/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const o = searchParams.get("open");
    if (o) { setActive(o); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/hangouts");
      setPosts(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      const payload = { content: content.trim(), image: image || null, mentions: parseMentions(content, members) };
      if (canPoll && usePoll) {
        const p = pollPayload(pollQuestions);
        if (p) payload.poll = p;
      }
      await api.post("/hangouts", payload);
      setContent(""); setImage(""); setComposer(false);
      setUsePoll(false); setPollQuestions([{ type: "mc", text: "", options: ["", ""] }]);
      toast.success("Posted to Hangouts");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  const toggleLike = async (post, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/hangouts/${post.id}/like`);
      setPosts((ps) => ps.map((p) => p.id === post.id ? { ...p, liked: data.liked, like_count: data.like_count } : p));
    } catch {}
  };

  const openEdit = (post, e) => {
    e.stopPropagation();
    setEditPost(post);
    setEditContent(post.content || "");
    setEditImage(post.image || "");
  };

  const saveEdit = async () => {
    if (!editContent.trim()) return;
    setSavingEdit(true);
    try {
      await api.patch(`/hangouts/${editPost.id}`, { content: editContent.trim(), image: editImage || null });
      setEditPost(null);
      toast.success("Post updated");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  if (active) return <Thread postId={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <div>
      <AppHeader title="Hangouts" />
      <div className="px-5 py-5 lg:px-8 lg:py-8 lg:max-w-4xl lg:mx-auto space-y-4 lg:space-y-5">
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin gold-text" /></div>
        ) : posts.length === 0 ? (
          <Empty text="No hangouts yet. Start the conversation!" />
        ) : posts.map((p) => (
          <motion.div key={p.id} whileTap={{ scale: 0.99 }} onClick={() => setActive(p.id)}
            data-testid={`hangout-post-${p.id}`}
            className="card-surface rounded-2xl p-4 lg:p-6 cursor-pointer">
            <div className="flex items-start gap-3">
              <Avatar name={p.author?.name} />
              <div className="flex-1">
                <div className="text-sm font-semibold">{p.author?.name}</div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{ago(p.created_at)}</div>
              </div>
              {p.author?.id === user?.id && (
                <button onClick={(e) => openEdit(p, e)} data-testid={`edit-hangout-${p.id}`}
                  className="h-8 w-8 rounded-full grid place-items-center" style={{ background: "var(--overlay)" }}>
                  <Pencil size={14} style={{ color: "var(--text-secondary)" }} />
                </button>
              )}
            </div>
            <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>{p.content}</p>
            {p.image && <img src={fileUrl(p.image)} alt="" className="mt-3 rounded-xl w-full object-cover max-h-72" />}
            {p.video && <FeedVideo path={p.video} className="max-h-72" testid={`hangout-video-${p.id}`} />}
            {p.poll && <div onClick={(e) => e.stopPropagation()}><Poll poll={p.poll} kind="hangout" id={p.id} userId={user?.id} testidPrefix={`hangout-poll-${p.id}`} /></div>}
            <div className="flex items-center gap-4 mt-3 pt-3" style={{ borderTop: "1px solid var(--hairline)" }}>
              <button onClick={(e) => toggleLike(p, e)} data-testid={`like-btn-${p.id}`} className="flex items-center gap-1.5">
                <Heart size={17} fill={p.liked ? "var(--accent)" : "none"} style={{ color: p.liked ? "var(--accent)" : "var(--text-secondary)" }} />
                <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{p.like_count}</span>
              </button>
              <button onClick={(e) => { e.stopPropagation(); setActive(p.id); }} data-testid={`comment-btn-${p.id}`}
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5" style={{ background: "var(--overlay)" }}>
                <MessageSquarePlus size={16} style={{ color: "var(--text-secondary)" }} />
                <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>Comment ({p.reply_count})</span>
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <Fab onClick={() => setComposer(true)} testid="new-hangout-fab" />

      <BottomSheet open={!!editPost} onClose={() => setEditPost(null)} title="Edit Post">
        <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={4}
          placeholder="Update your post…" data-testid="edit-hangout-content-input"
          className="w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--accent)]"
          style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
        {editImage && <img src={fileUrl(editImage)} alt="" className="mt-3 rounded-xl w-full max-h-56 object-cover" />}
        <div className="mt-3 flex gap-2">
          <div className="flex-1"><MediaUploader label={editImage ? "Replace photo" : "Attach photo"} testid="edit-hangout-image-upload" onUploaded={setEditImage} /></div>
          {editImage && <button onClick={() => setEditImage("")} className="rounded-xl px-3 text-xs" style={{ background: "var(--overlay)", color: "var(--destructive)" }} data-testid="edit-remove-image">Remove</button>}
        </div>
        <motion.button whileTap={{ scale: 0.97 }} onClick={saveEdit} disabled={savingEdit}
          data-testid="edit-hangout-save-btn"
          className="w-full mt-4 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
          style={{ background: "var(--accent)", color: "#fff" }}>
          {savingEdit && <Loader2 size={16} className="animate-spin" />} Save Changes
        </motion.button>
      </BottomSheet>

      <BottomSheet open={composer} onClose={() => setComposer(false)} title="New Hangout">
        <MentionInput as="textarea" value={content} onChange={setContent} members={members} rows={4}
          placeholder="Share something with the family… use @ to tag" testid="hangout-content-input"
          className="w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--accent)]"
          style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
        {image && <img src={fileUrl(image)} alt="" className="mt-3 rounded-xl w-full max-h-56 object-cover" />}
        <div className="mt-3">
          <MediaUploader label="Attach photo" testid="hangout-image-upload" onUploaded={setImage} />
        </div>
        {canPoll && (
          <div className="mt-3 rounded-xl p-3" style={{ background: "var(--overlay)" }}>
            <label className="flex items-center justify-between">
              <span className="text-sm flex items-center gap-1.5"><BarChart3 size={15} className="gold-text" /> Add a poll</span>
              <input type="checkbox" checked={usePoll} onChange={(e) => setUsePoll(e.target.checked)}
                data-testid="hangout-poll-toggle" className="h-4 w-4 accent-[var(--accent)]" />
            </label>
            {usePoll && (
              <div className="mt-3">
                <PollComposer questions={pollQuestions} onChange={setPollQuestions} />
              </div>
            )}
          </div>
        )}
        <motion.button whileTap={{ scale: 0.97 }} onClick={submit} disabled={posting}
          data-testid="hangout-submit-btn"
          className="w-full mt-4 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
          style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
          {posting && <Loader2 size={16} className="animate-spin" />} Post
        </motion.button>
      </BottomSheet>
    </div>
  );
}

export function Thread({ postId, onBack }) {
  const { user, family } = useAuth();
  const canPoll = family?.can_poll;
  const [post, setPost] = useState(null);
  const [reply, setReply] = useState("");
  const [replyImage, setReplyImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [members, setMembers] = useState([]);
  const [replyPoll, setReplyPoll] = useState(false);
  const [pollQuestions, setPollQuestions] = useState([{ type: "mc", text: "", options: ["", ""] }]);
  const fileRef = useRef();

  useEffect(() => { api.get("/families/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);

  const likeReply = async (r) => {
    try {
      const { data } = await api.post("/likes/toggle", { kind: "hangout_reply", id: r.id });
      setPost((p) => ({ ...p, replies: p.replies.map((x) => x.id === r.id ? { ...x, liked: data.liked, like_count: data.like_count } : x) }));
    } catch {}
  };

  const pickImage = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReplyImage(data.path);
      toast.success("Image attached");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const load = useCallback(async () => {
    const { data } = await api.get(`/hangouts/${postId}`);
    setPost(data);
  }, [postId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    document.body.classList.add("hide-bottom-nav");
    return () => document.body.classList.remove("hide-bottom-nav");
  }, []);

  const send = async () => {
    if (!reply.trim() && !replyImage && !replyPoll) return;
    setSending(true);
    try {
      const payload = { content: reply.trim(), image: replyImage || null, mentions: parseMentions(reply, members) };
      if (canPoll && replyPoll) { const p = pollPayload(pollQuestions); if (p) payload.poll = p; }
      await api.post(`/hangouts/${postId}/replies`, payload);
      setReply(""); setReplyImage(""); setReplyPoll(false);
      setPollQuestions([{ type: "mc", text: "", options: ["", ""] }]);
      toast.success("Reply added");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="flex flex-col" style={{ height: "100dvh" }}>
      <div className="shrink-0 z-40 glass hairline px-5 py-4 flex items-center gap-3">
        <button onClick={onBack} data-testid="thread-back-btn"><ArrowLeft size={22} /></button>
        <h1 className="font-serif-display text-2xl">Thread</h1>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
      {!post ? <div className="grid place-items-center py-20"><Loader2 className="animate-spin gold-text" /></div> : (
        <div className="px-5 py-5">
          <div className="card-surface rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <Avatar name={post.author?.name} />
              <div>
                <div className="text-sm font-semibold">{post.author?.name}</div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{ago(post.created_at)}</div>
              </div>
            </div>
            <p className="text-base mt-3 whitespace-pre-wrap">{post.content}</p>
            {post.image && <img src={fileUrl(post.image)} alt="" className="mt-3 rounded-xl w-full object-cover" />}
            {post.video && <FeedVideo path={post.video} className="max-h-96" testid="hangout-detail-video" />}
          </div>

          <div className="mt-5 mb-2 text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
            {post.replies.length} {post.replies.length === 1 ? "Reply" : "Replies"}
          </div>
          <div className="space-y-3 pl-3" style={{ borderLeft: "1px solid rgba(124,131,255,0.25)" }}>
            {post.replies.map((r) => (
              <motion.div key={r.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                data-testid={`reply-${r.id}`} className="rounded-xl p-3" style={{ background: "var(--overlay)" }}>
                <div className="flex items-center gap-2">
                  <Avatar name={r.author?.name} sm />
                  <span className="text-xs font-semibold">{r.author?.name}</span>
                  <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{ago(r.created_at)}</span>
                </div>
                <p className="text-sm mt-2 whitespace-pre-wrap">{r.content}</p>
                {r.image && <img src={fileUrl(r.image)} alt="" className="mt-2 rounded-lg w-full max-h-60 object-cover" data-testid={`reply-image-${r.id}`} />}
                {r.poll && <Poll poll={r.poll} kind="hangout_reply" id={r.id} userId={user?.id} testidPrefix={`reply-poll-${r.id}`} />}
                <button onClick={() => likeReply(r)} data-testid={`reply-like-${r.id}`} className="flex items-center gap-1.5 mt-2">
                  <Heart size={14} fill={r.liked ? "var(--accent)" : "none"} style={{ color: r.liked ? "var(--accent)" : "var(--text-muted)" }} />
                  <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{r.like_count || 0}</span>
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      )}
      </div>
      <div className="shrink-0 glass z-[60]" style={{ paddingBottom: "calc(env(safe-area-inset-bottom,0px))" }}>
        {replyPoll && (
          <div className="px-3 pt-3 max-h-72 overflow-y-auto no-scrollbar">
            <PollComposer questions={pollQuestions} onChange={setPollQuestions} />
          </div>
        )}
        {replyImage && (
          <div className="px-3 pt-2 flex items-center gap-2">
            <img src={fileUrl(replyImage)} alt="" className="h-14 w-14 rounded-lg object-cover" />
            <button onClick={() => setReplyImage("")} className="text-xs" style={{ color: "var(--destructive)" }} data-testid="remove-reply-image">Remove</button>
          </div>
        )}
        <div className="p-3 flex items-end gap-2">
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="reply-image-file" />
          <button onClick={pickImage} disabled={uploading} data-testid="reply-image-btn"
            className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: "var(--overlay)" }}>
            {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} style={{ color: "var(--text-secondary)" }} />}
          </button>
          {canPoll && (
            <button onClick={() => setReplyPoll((v) => !v)} data-testid="reply-poll-btn"
              className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: replyPoll ? "var(--accent)" : "var(--overlay)" }}>
              <BarChart3 size={18} style={{ color: replyPoll ? "#fff" : "var(--text-secondary)" }} />
            </button>
          )}
          <MentionInput value={reply} onChange={setReply} members={members} onEnter={send} wrapperClassName="flex-1"
            placeholder="Write a reply… use @ to tag" testid="reply-input"
            className="w-full rounded-2xl px-4 py-3 text-sm outline-none"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
          <button onClick={send} disabled={sending} data-testid="reply-send-btn"
            className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Avatar({ name, sm }) {
  const s = sm ? "h-7 w-7 text-xs" : "h-10 w-10 text-sm";
  return (
    <div className={`${s} rounded-full grid place-items-center gold-border shrink-0`} style={{ background: "var(--bg-elevated)" }}>
      <span className="font-serif-display gold-text">{initial(name)}</span>
    </div>
  );
}

export function Fab({ onClick, testid }) {
  return (
    <motion.button whileTap={{ scale: 0.9 }} onClick={onClick} data-testid={testid}
      className="fixed z-40 h-14 w-14 rounded-full grid place-items-center fv-fab"
      style={{ bottom: 96, right: 20, background: "var(--accent)", color: "#fff", boxShadow: "0 8px 24px rgba(124,131,255,0.4)" }}>
      <Plus size={26} />
    </motion.button>
  );
}

export function Empty({ text }) {
  return <div className="text-center py-20 px-8" style={{ color: "var(--text-muted)" }}>{text}</div>;
}
